import { Cell, Column, Row, Table, TableBody, TableHeader } from '@eleveo/components-react';
import { TFunction } from 'i18next';
import React from 'react';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { SkillWithUsed } from '../../graphql/components';
import { TableActionsMenu } from './TableActionsMenu';
import { LIST_OF_SKILLS_CONTAINER_TAG } from './constants';

import type { ColumnProps } from '@eleveo/components-react';

export interface SkillsTableProps {
  onDeleteSkill: (s: SkillWithUsed) => void;
  onEditSkill: (s: SkillWithUsed) => void;
  skills: SkillWithUsed[];
  t: TFunction;
  activeId?: string;
}

export const SkillsTable: React.FC<SkillsTableProps> = ({ t, skills, onEditSkill, onDeleteSkill, activeId }) => {
  const { userHasPermissions } = useAuth();
  const hasEditPermission = userHasPermissions([Permissions.EditSkill]);
  const hasDeletePermission = userHasPermissions([Permissions.DeleteSkill]);

  const noPermission = !hasEditPermission && !hasDeletePermission;

  const columns: (ColumnProps<any> & { key: React.Key })[] = [{ key: 'name', children: t('skill.name') }];

  if (!noPermission) {
    columns.push({
      key: 'actions',
      children: t('layout.action.actions'),
      isActions: true,
      hideHeader: true,
    });
  }

  return (
    <Table
      aria-label={t('skill.skills')}
      highlightedKey={activeId}
      isStickyHeader
      testId={LIST_OF_SKILLS_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>{(column) => <Column {...column} key={column.key} />}</TableHeader>

      <TableBody items={skills}>
        {(item) => (
          <Row key={item.id}>
            {(columnKey) => (
              <Cell>
                {columnKey === 'name' && <strong>{item.name}</strong>}
                {columnKey === 'actions' && (
                  <TableActionsMenu
                    onEditSkill={onEditSkill}
                    onDeleteSkill={onDeleteSkill}
                    hasDeletePermission={hasDeletePermission}
                    hasEditPermission={hasEditPermission}
                    skillItem={item}
                  />
                )}
              </Cell>
            )}
          </Row>
        )}
      </TableBody>
    </Table>
  );
};
